import { z } from 'zod';

/** Una opción de «Cómo nos encontró». La clave es estable; la etiqueta se puede renombrar. */
export interface OrigenConfig {
  clave: string;
  etiqueta: string;
  activo: boolean;
}

export const ORIGENES_POR_DEFECTO: OrigenConfig[] = [
  { clave: 'facebook', etiqueta: 'Facebook', activo: true },
  { clave: 'instagram', etiqueta: 'Instagram', activo: true },
  { clave: 'twitter_x', etiqueta: 'Twitter (X)', activo: true },
  { clave: 'whatsapp', etiqueta: 'WhatsApp', activo: true },
  { clave: 'google_maps', etiqueta: 'Google Maps', activo: true },
  { clave: 'redes_sociales', etiqueta: 'Otras redes sociales', activo: true },
  { clave: 'recomendacion_cliente', etiqueta: 'Recomendación de un cliente', activo: true },
  { clave: 'recomendacion_trabajador', etiqueta: 'Recomendación de un trabajador', activo: true },
  { clave: 'paso_por_local', etiqueta: 'Pasó por el local', activo: true },
  { clave: 'otro', etiqueta: 'Otro', activo: true },
];

export const CLAVE_ORIGEN = /^[a-z0-9_]{1,60}$/;

const mismaEtiqueta = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

export const origenesSchema = z
  .array(z.object({ clave: z.string().regex(CLAVE_ORIGEN), etiqueta: z.string().trim().min(1).max(60), activo: z.boolean() }))
  .min(1)
  .max(50)
  .refine((l) => new Set(l.map((o) => o.clave)).size === l.length, 'Claves repetidas')
  .refine((l) => new Set(l.map((o) => o.etiqueta.toLowerCase())).size === l.length, 'Etiquetas repetidas')
  .refine((l) => l.some((o) => o.activo), 'Debe quedar al menos una opción activa');

/** Clave a partir de la etiqueta: minúsculas, sin acentos, `_` entre palabras. */
export function slugOrigen(etiqueta: string): string {
  return etiqueta
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60)
    .replace(/_+$/, '');
}

/** Agrega una opción; si ya existía quitada con la misma clave, la vuelve a ofrecer. */
export function agregarOrigen(lista: OrigenConfig[], etiqueta: string): OrigenConfig[] {
  const texto = etiqueta.trim();
  const clave = slugOrigen(texto);
  if (!clave) throw new Error('Etiqueta inválida');
  if (lista.some((o) => o.activo && (o.clave === clave || mismaEtiqueta(o.etiqueta, texto)))) throw new Error('Ya existe esa opción');
  // Una opción quitada con la misma etiqueta o la misma clave se vuelve a ofrecer (conserva su clave).
  const quitada = lista.find((o) => mismaEtiqueta(o.etiqueta, texto)) ?? lista.find((o) => o.clave === clave);
  if (quitada) return lista.map((o) => (o.clave === quitada.clave ? { ...o, etiqueta: texto, activo: true } : o));
  return [...lista, { clave, etiqueta: texto, activo: true }];
}

/** Vuelve a ofrecer una opción quitada, con su clave y etiqueta. */
export function reactivarOrigen(lista: OrigenConfig[], clave: string): OrigenConfig[] {
  return lista.map((o) => (o.clave === clave ? { ...o, activo: true } : o));
}

export function renombrarOrigen(lista: OrigenConfig[], clave: string, etiqueta: string): OrigenConfig[] {
  return lista.map((o) => (o.clave === clave ? { ...o, etiqueta } : o));
}

/** Quitar = dejar de ofrecer: los clientes que ya la tienen conservan su etiqueta. */
export function quitarOrigen(lista: OrigenConfig[], clave: string): OrigenConfig[] {
  if (!lista.some((o) => o.activo && o.clave !== clave)) throw new Error('Debe quedar al menos una opción activa');
  return lista.map((o) => (o.clave === clave ? { ...o, activo: false } : o));
}

export const origenesActivos = (lista: OrigenConfig[]) => lista.filter((o) => o.activo);

export function etiquetaOrigen(lista: OrigenConfig[], clave: string): string {
  return lista.find((o) => o.clave === clave)?.etiqueta ?? clave;
}
